/**
 * AddCxP.js — Formulario nueva Factura CxP
 * UI idéntico a AddInvoices: invoice-container (2fr 1fr), invoice-details (izq),
 * filial-details (der), invoice-footer (bottom full-width).
 * CSS: addInvoice.module.css (mismas clases que AddInvoices).
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import styles from "../../invoices/styles/addInvoice.module.css";
import { MonedaSelector } from "../../components/MonedaSelector";
import { useTipoCambio } from "../../services/useTipoCambio";

function addDays(dateStr, days) {
  const d = new Date(dateStr); d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

const fmt = (n) => Number(n || 0).toLocaleString("es-CR", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:5,
      color:"#f87171", fontSize:"0.77rem", marginTop:4, fontWeight:500 }}>
      <i className="fa-solid fa-circle-exclamation" style={{ fontSize:"0.72rem" }}/>
      {msg}
    </div>
  );
}

export default function AddCxP() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];

  function conceptoAuto(nombreGasto, fechaEmision) {
    if (!nombreGasto) return "";
    let month, year;
    if (fechaEmision) {
      [year, month] = fechaEmision.split("-").map(Number); month -= 1;
    } else { const d = new Date(); month = d.getMonth(); year = d.getFullYear(); }
    const date = new Date(year, month, 1);
    const locale = i18n.resolvedLanguage === "en" ? "en-US" : "es-CR";
    const monthName = new Intl.DateTimeFormat(locale, { month: "long" }).format(date);
    return `${nombreGasto} — ${monthName} ${year}`;
  }

  const [form, setForm] = useState({
    fecha_emision:           today,
    fecha_vencimiento:       today,
    concepto:                "",
    monto:                   "",
    numero_doc_proveedor:    "",
    cuenta_proveedor:        null,
    cuenta_proveedor_nombre: "",
    cuenta_gasto:            null,
    cuenta_gasto_nombre:     "",
    moneda:                  "CRC",
    tipo_cambio:             1,
  });
  const { tipoCambio, loading: loadingTC } = useTipoCambio();

  // Auto-populate TC from BCCR when switching to USD; reset for CRC
  useEffect(() => {
    if (form.moneda === "USD" && tipoCambio?.tipo_venta) {
      setForm(p => ({ ...p, tipo_cambio: Number(tipoCambio.tipo_venta) }));
    } else if (form.moneda === "CRC") {
      setForm(p => ({ ...p, tipo_cambio: 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.moneda, tipoCambio]);

  const [cuentasPasivo,  setCuentasPasivo]  = useState([]);
  const [cuentasGasto,   setCuentasGasto]   = useState([]);
  const [proximoNumero,  setProximoNumero]  = useState("CP-…");
  const [errors,        setErrors]        = useState({});
  const [saving,        setSaving]        = useState(false);
  const [apiErr,        setApiErr]        = useState("");
  const [resultado,     setResultado]     = useState(null);

  useEffect(() => {
    apiFetch("/api/finance/cxp/proximo-numero/")
      .then(r => r.json())
      .then(d => setProximoNumero(d.proximo ?? "CP-…"))
      .catch(() => {});

    apiFetch("/api/finance/catalogo/detalle/?tipo=PASIVO&padre_nombre=Cuentas+por+Pagar+Locales")
      .then(r => r.json())
      .then(d => setCuentasPasivo(Array.isArray(d) ? d : (d.results ?? [])));

    apiFetch("/api/finance/catalogo/detalle/?tipo=GASTO")
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.results ?? []);
        setCuentasGasto(list);
        if (list.length > 0) {
          const p = list[0];
          setForm(prev => ({ ...prev,
            cuenta_gasto: p.id, cuenta_gasto_nombre: p.nombre,
            concepto: conceptoAuto(p.nombre, prev.fecha_emision),
          }));
        }
      });
  }, []);

  function set(f, v) { setForm(p => ({ ...p, [f]: v })); }

  function handleFechaEmision(fecha) {
    setForm(p => ({
      ...p, fecha_emision: fecha,
      fecha_vencimiento: fecha ?? "",
      concepto: conceptoAuto(p.cuenta_gasto_nombre, fecha),
    }));
  }

  function handleCuentaGasto(e) {
    const id = Number(e.target.value);
    const obj = cuentasGasto.find(c => c.id === id);
    setForm(p => ({
      ...p, cuenta_gasto: id || null, cuenta_gasto_nombre: obj?.nombre ?? "",
      concepto: conceptoAuto(obj?.nombre ?? "", p.fecha_emision),
    }));
  }

  function handleCuentaProveedor(e) {
    const id = Number(e.target.value);
    const obj = cuentasPasivo.find(c => c.id === id);
    setForm(p => ({ ...p, cuenta_proveedor: id || null, cuenta_proveedor_nombre: obj?.nombre ?? "" }));
  }

  function validate() {
    const e = {};
    if (!form.numero_doc_proveedor.trim()) e.numero_doc_proveedor = t("cxp.nDocReq");
    if (!form.cuenta_proveedor)            e.cuenta_proveedor     = t("cxp.proveedorReq");
    if (!form.cuenta_gasto)                e.cuenta_gasto         = t("cxp.tipoGastoReq");
    if (!form.concepto.trim())             e.concepto             = t("cxp.conceptoReq");
    if (!form.fecha_emision)               e.fecha_emision        = t("cxp.fechaEmisionReq");
    if (!form.fecha_vencimiento)           e.fecha_vencimiento    = t("cxp.fechaVencimientoReq");
    if (form.fecha_emision && form.fecha_vencimiento &&
        form.fecha_vencimiento < form.fecha_emision)
                                           e.fecha_vencimiento    = t("cxp.fechaVencimientoPosterior");
    if (!form.monto || Number(form.monto) <= 0) e.monto           = t("cxp.montoReq");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setApiErr("");
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await apiFetch("/api/finance/cxp/", {
        method: "POST",
        body: JSON.stringify({
          fecha_emision:        form.fecha_emision,
          fecha_vencimiento:    form.fecha_vencimiento,
          concepto:             form.concepto.trim(),
          monto:                Number(form.monto),
          numero_doc_proveedor: form.numero_doc_proveedor.trim(),
          cuenta_proveedor:     form.cuenta_proveedor,
          cuenta_gasto:         form.cuenta_gasto,
          moneda:               form.moneda,
          tipo_cambio:          Number(form.tipo_cambio) || 1,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        const k = Object.keys(err)[0];
        throw new Error(k ? `${k}: ${Array.isArray(err[k]) ? err[k][0] : err[k]}` : "Error al guardar");
      }
      setResultado(await res.json());
      setErrors({});
      apiFetch("/api/finance/cxp/proximo-numero/")
        .then(r => r.json()).then(d => setProximoNumero(d.proximo ?? "CP-…"));
    } catch (ex) { setApiErr(ex.message); }
    finally { setSaving(false); }
  }

  function handleReset(e) {
    e.preventDefault();
    const autoConcepto = conceptoAuto(form.cuenta_gasto_nombre, today);
    setForm({ fecha_emision: today, fecha_vencimiento: addDays(today, 30),
      concepto: autoConcepto, monto: "", numero_doc_proveedor: "",
      cuenta_proveedor: null, cuenta_proveedor_nombre: "",
      cuenta_gasto: form.cuenta_gasto, cuenta_gasto_nombre: form.cuenta_gasto_nombre,
      moneda: "CRC", tipo_cambio: 1,
    });
    setErrors({}); setApiErr("");
  }

  // ── Pantalla éxito ──────────────────────────────────────────────────────
  if (resultado) {
    return (
      <>
        <span>
          <Link to="/">{t("common.inicio")}</Link> /{" "}
          <Link to="/finanzas/cxp/facturas">{t("cxp.titulo")}</Link> /{" "}
          <b>{t("cxp.registrada")}</b>
        </span>
        <h2>{t("cxp.generarDocumento")}</h2>
        <div style={{ background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.3)",
          borderLeft:"4px solid #4ade80", borderRadius:8, padding:"12px 18px", marginBottom:16,
          display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
          <span style={{ color:"#4ade80", fontWeight:500, fontSize:"0.9rem" }}>
            <i className="fa-solid fa-circle-check" style={{ marginRight:8 }}/>
            {t("cxp.facturaCxPRegistrada", { numero: resultado.numero_factura })}
          </span>
          <button onClick={() => setResultado(null)}
            style={{ background:"none", border:"none", color:"#4ade80", cursor:"pointer" }}>✕</button>
        </div>
        <div className={styles["invoice-container"]}>
          <div className={styles["invoice-details"]} style={{ padding:32, textAlign:"center" }}>
            <div style={{ fontSize:"2.5rem", marginBottom:12 }}>✅</div>
            <h2 style={{ fontSize:16, fontWeight:700, color:"var(--text-primary)", margin:"0 0 4px" }}>
              {resultado.numero_factura}
            </h2>
            <p style={{ color:"var(--text-secondary)", fontSize:14, margin:"0 0 20px" }}>
              {t("cxp.facturaRegistradaOkMsg")}
            </p>
            <div style={{ background:"rgba(74,222,128,0.07)", border:"1px solid rgba(74,222,128,0.25)",
              borderRadius:6, padding:"12px 16px", textAlign:"left", marginBottom:20 }}>
              <div style={{ color:"#4ade80", fontWeight:600, fontSize:14, marginBottom:6 }}>
                <i className="fa-solid fa-book-open" style={{ marginRight:6 }}/>{t("cxp.asientoDiarioGenerado")}
              </div>
              <div style={{ color:"var(--text-secondary)", fontSize:13, lineHeight:1.8, fontFamily:"monospace" }}>
                <span style={{ color:"#f87171" }}>{t("asientos.debe").toUpperCase()}</span>
                {"  "}{form.cuenta_gasto_nombre}{"  "}₡{fmt(form.monto)}<br/>
                <span style={{ color:"#60a5fa" }}>{t("asientos.haber").toUpperCase()}</span>
                {"  "}{form.cuenta_proveedor_nombre}{"  "}₡{fmt(form.monto)}
              </div>
            </div>
          </div>
          <div className={styles["filial-details"]} style={{ display:"flex", flexDirection:"column", gap:10, justifyContent:"center", alignItems:"center" }}>
            <button onClick={() => { setResultado(null); handleReset({ preventDefault:()=>{} }); }}
              className={`${styles.btn} ${styles["btn-clean-form"]}`} style={{ width:"80%" }}>
              {t("cxp.nuevaFactura")}
            </button>
            <button onClick={() => navigate("/finanzas/cxp/facturas")}
              className={`${styles.btn} ${styles["btn-save"]}`} style={{ width:"80%" }}>
              {t("cxp.verCxP")}
            </button>
          </div>
          <div className={styles["invoice-footer"]}/>
        </div>
      </>
    );
  }

  const errorCount = Object.keys(errors).length;
  const montoNum = Number(form.monto) || 0;

  return (
    <>
      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <Link to="/finanzas/cxp">
          <span className={styles.inactive}> {t("cxp.titulo")} /</span>
        </Link>
        <Link to="/finanzas/cxp/facturas">
          <span className={styles.inactive}> {t("cxp.breadcrumbFacturas")} /</span>
        </Link>
        <span className={styles.active}> {t("common.agregar")}</span>
      </span>

      <h2 className="mt-2 me-2 p-0 title">{t("cxp.generarFactura")}</h2>

      {/* Error count banner */}
      {errorCount > 0 && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px", marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8,
            color:"#f87171", fontWeight:600, marginBottom:6 }}>
            <i className="fa-solid fa-triangle-exclamation"/>
            {t("common.hay")} {errorCount} {t("cxp.camposConError")}
          </div>
          <ul style={{ margin:0, paddingLeft:20, color:"#fca5a5", fontSize:"0.83rem" }}>
            {Object.values(errors).map((msg, i) => <li key={i}>{msg}</li>)}
          </ul>
        </div>
      )}

      {/* API error */}
      {apiErr && (
        <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.35)",
          borderLeft:"4px solid #ef4444", borderRadius:8, padding:"12px 18px", marginBottom:16,
          color:"#f87171", fontSize:"0.88rem", display:"flex", alignItems:"center", gap:8 }}>
          <i className="fa-solid fa-circle-exclamation"/>{apiErr}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className={styles["invoice-container"]}>

          {/* ── Izquierda: Detalles del Documento ── */}
          <div className={styles["invoice-details"]}>
            <div className={styles["invoice-details-header"]}>
              <h1>
                <i className={`fa-solid fa-file-invoice-dollar ${styles["icon-invoice-details"]}`}/>
                {t("cxp.detallesDocumento")}
              </h1>
            </div>
            <div className={styles["invoice-details-body"]} style={{ marginTop:14 }}>

              {/* N° Doc Proveedor */}
              <div>
                <label>{t("cxp.numDocProveedor")} <span style={{ color:"#f87171" }}>*</span></label>
                <input className="form-control" type="text"
                  placeholder={t("cxp.nDocPlaceholder")}
                  value={form.numero_doc_proveedor}
                  onChange={e => set("numero_doc_proveedor", e.target.value)}
                  style={errors.numero_doc_proveedor ? { borderColor:"#ef4444" } : undefined}/>
                <FieldError msg={errors.numero_doc_proveedor}/>
                <div style={{ fontSize:"0.73rem", color:"var(--text-muted)", marginTop:3,
                  display:"flex", alignItems:"center", gap:5 }}>
                  <i className="fa-solid fa-hashtag" style={{ fontSize:"0.65rem" }}/>
                  {t("cxp.internoAutoNum")}
                  <span style={{ fontFamily:"monospace", color:"#f97316",
                    fontWeight:600 }}>{proximoNumero}</span>
                </div>
              </div>

              {/* Proveedor (HABER) */}
              <div>
                <label>{t("cxp.proveedor")} <span style={{ color:"#f87171" }}>*</span></label>
                <select className="form-select"
                  value={form.cuenta_proveedor ?? ""}
                  onChange={handleCuentaProveedor}
                  style={errors.cuenta_proveedor ? { borderColor:"#ef4444" } : undefined}>
                  <option value="">{t("cxp.nombreProveedor")}</option>
                  {cuentasPasivo.map(c => (
                    <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                  ))}
                </select>
                <FieldError msg={errors.cuenta_proveedor}/>
              </div>

              {/* Gasto (DEBE) */}
              <div>
                <label>{t("cxp.gasto")} <span style={{ color:"#f87171" }}>*</span></label>
                <select className="form-select"
                  value={form.cuenta_gasto ?? ""}
                  onChange={handleCuentaGasto}
                  style={errors.cuenta_gasto ? { borderColor:"#ef4444" } : undefined}>
                  <option value="">{t("cxp.seleccionaTipoGasto")}</option>
                  {cuentasGasto.map(c => (
                    <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                  ))}
                </select>
                <FieldError msg={errors.cuenta_gasto}/>
              </div>

              {/* Concepto (auto, editable) */}
              <div>
                <label style={{ display:"flex", alignItems:"center", gap:6 }}>
                  {t("cxp.concepto")}
                  <span style={{ fontSize:"0.68rem", color:"var(--text-secondary)",
                    background:"var(--bg-card)", border:"1px solid var(--border-card)",
                    borderRadius:4, padding:"1px 6px", fontWeight:400 }}>
                    {t("cxp.autoBadge")}
                  </span>
                </label>
                <input className="form-control"
                  placeholder={t("cxp.conceptoPlaceholder")}
                  value={form.concepto}
                  onChange={e => set("concepto", e.target.value)}
                  style={errors.concepto ? { borderColor:"#ef4444" } : undefined}/>
                <FieldError msg={errors.concepto}/>
                <div style={{ fontSize:"0.73rem", color:"var(--text-muted)", marginTop:3 }}>
                  {t("cxp.editarManualmente")}
                </div>
              </div>

              {/* Fechas */}
              <div className="row">
                <div className="col">
                  <label>{t("cxp.fechaEmision")} <span style={{ color:"#f87171" }}>*</span></label>
                  <input type="date" className="form-control"
                    value={form.fecha_emision}
                    onChange={e => handleFechaEmision(e.target.value)}
                    style={errors.fecha_emision ? { borderColor:"#ef4444" } : undefined}/>
                  <FieldError msg={errors.fecha_emision}/>
                </div>
                <div className="col">
                  <label>{t("cxp.fechaVencimiento")} <span style={{ color:"#f87171" }}>*</span></label>
                  <input type="date" className="form-control"
                    value={form.fecha_vencimiento}
                    onChange={e => set("fecha_vencimiento", e.target.value)}
                    style={errors.fecha_vencimiento ? { borderColor:"#ef4444" } : undefined}/>
                  <FieldError msg={errors.fecha_vencimiento}/>
                </div>
              </div>

              {/* Monto */}
              <div>
                <label>{t("cxp.monto")} <span style={{ color:"#f87171" }}>*</span></label>
                <input type="number" className="form-control"
                  placeholder="0.00" min="0.01" step="0.01"
                  value={form.monto}
                  onChange={e => set("monto", e.target.value)}
                  style={errors.monto ? { borderColor:"#ef4444" } : undefined}/>
                <FieldError msg={errors.monto}/>
              </div>

              {/* Moneda */}
              <MonedaSelector
                moneda={form.moneda}
                onMonedaChange={(m) => set("moneda", m)}
                tipoCambio={form.tipo_cambio}
                onTCChange={(tc) => set("tipo_cambio", tc)}
                loadingTC={loadingTC}
                tcFuente={tipoCambio?.fuente}
                monto={Number(form.monto) || 0}
              />

            </div>
          </div>

          {/* ── Derecha: Resumen ── */}
          <div className={styles["filial-details"]}>
            <div className={styles["filial-details-header"]}>
              <h1>
                <i className={`fa-solid fa-house ${styles["icon-filial-details"]}`}/>
                {t("cxp.resumenGasto")}
              </h1>
            </div>

            <div className={styles["invoice-details-body"]} style={{ marginTop:14 }}>
              <div className={styles["filial-details-summary"]}>
                <div className={styles["filial-summary-relevant"]}>
                  <h2>{t("registros.resumenRapido")}</h2>
                  {[
                    [t("cxp.numDocProveedor"), form.numero_doc_proveedor || "—"],
                    [t("cxp.proveedor"),       form.cuenta_proveedor_nombre || "—"],
                    [t("cxp.gasto"),           form.cuenta_gasto_nombre || "—"],
                    [t("cxp.fechaEmision"),    form.fecha_emision || "—"],
                    [t("cxp.fechaVencimiento"),form.fecha_vencimiento || "—"],
                  ].map(([label, val], i) => (
                    <div key={i} style={{ display:"flex", justifyContent:"space-between",
                      marginBottom:4, fontSize:14 }}>
                      <span style={{ color:"var(--text-muted)" }}>{label}</span>
                      <span style={{ color:"var(--text-secondary)", maxWidth:130,
                        textAlign:"right", overflow:"hidden", textOverflow:"ellipsis",
                        whiteSpace:"nowrap" }}>{val}</span>
                    </div>
                  ))}
                </div>
                <div className={styles["filial-summary-important"]} style={{ marginTop:12 }}>
                  <h2>{t("cxp.totalAPagar")}</h2>
                  {form.moneda === "USD" ? (
                    <>
                      <span style={{ fontSize:"0.85rem", color:"#60a5fa" }}>
                        ${fmt(montoNum)} USD
                      </span>
                      <span style={{ fontSize:"0.78rem", color:"var(--text-muted)", display:"block" }}>
                        ≈ ₡{fmt(montoNum * (Number(form.tipo_cambio) || 1))}
                      </span>
                    </>
                  ) : (
                    <span>₡{fmt(montoNum)}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className={styles["invoice-footer"]}>
            <div className={styles.actions}>
              <button type="button" onClick={handleReset}
                className={`${styles.btn} ${styles["btn-clean-form"]}`}>
                <i className="fa-solid fa-rotate-left"/> {t("cxp.limpiarFormulario")}
              </button>
              <button type="submit" disabled={saving}
                className={`${styles.btn} ${styles["btn-save"]}`}
                style={{ opacity: saving ? 0.7 : 1 }}>
                {saving
                  ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("cxp.guardando")}</>
                  : <><i className="fa-solid fa-lock"/> {t("common.guardar")}</>}
              </button>
            </div>
          </div>

        </div>
      </form>
    </>
  );
}
